/**
 * Build a vault capture row from a WA webhook/listener message (plan 1.2).
 *
 * The live capture paths — incoming (`handleNewIncomingWaMessageThunk`)
 * and outgoing (`handleWaEvent` own-message branch) — both receive a WA message
 * object and need the same field-extraction to produce a vault row. This
 * is that shared, pure extraction (`fromMe` is DERIVED from the message,
 * so the same helper serves both directions).
 *
 * The incoming path additionally enriches the base row with
 * asynchronously-fetched voice-note / thumbnail bytes; this helper
 * produces only the synchronous base row.
 */
import { isBucketMediaType, type VaultCaptureInput } from './types';
import { resolveWaMsgSerializedId } from './resolve-msg-id';
import { decodeCallerTokenFromMessageSecret } from '@bt-common/scheduling';

/**
 * The caller correlation token on this message, or undefined.
 *
 * Gated on the caller-token marker, never a bare decode: on a message we did
 * not send, `messageSecret` is WhatsApp's E2E material and must not be stored,
 * let alone handed back through the API. Tolerates the shapes WAWeb hands the
 * bytes back in (Uint8Array, array, or a `{0:..,1:..}` object left behind by a
 * serializer).
 */
const callerTokenOf = (msg: any): string | undefined => {
  // rev 3.3 §6.6: `secret` only on our OWN (fromMe) messages. The vault feeds
  // v1 reads, and the listener payloads now carry the caller token for ALL
  // messages (the backend verifies the binding) — an inbound message with a
  // copied/forwarded secret must not be stored as one of our sends. Same
  // derivation as the extension's isWaMessageFromMe: MsgKey first, then the
  // flat flag; anything not explicitly true is not ours.
  const keyFromMe = msg?.id?.fromMe;
  const fromMe = typeof keyFromMe === 'boolean' ? keyFromMe : msg?.fromMe === true;
  if (!fromMe) return undefined;
  // ALREADY DECODED, and this is the normal case for outgoing messages: the
  // own-message listener payload crosses a postMessage boundary, where a
  // Uint8Array `messageSecret` does not survive — so the page realm decodes it
  // to a plain string first (wa-communication-layer-client
  // `callerTokenOfMessage`). Reading it here is what makes the outgoing capture
  // carry the token at all.
  if (typeof msg?.secret === 'string' && msg.secret.length > 0) return msg.secret;
  const raw = msg?.messageSecret ?? msg?.messageContextInfo?.messageSecret;
  if (!raw) return undefined;
  try {
    const bytes =
      raw instanceof Uint8Array
        ? raw
        : Array.isArray(raw)
          ? Uint8Array.from(raw)
          : typeof raw === 'object'
            ? Uint8Array.from(Object.values(raw as Record<string, number>))
            : null;
    return (bytes ? decodeCallerTokenFromMessageSecret(bytes) : null) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Extract a `VaultCaptureInput` from a WA message object, or `null` when
 * the message has no serialized id (nothing to key on). Timestamp falls
 * back to `Date.now()` when WA didn't stamp one.
 */
export const buildVaultRowFromWaMessage = (
  msg: any,
): VaultCaptureInput | null => {
  // WA Web's top-level MsgKey._serialized is undefined on current builds;
  // resolveWaMsgSerializedId falls back to String(id) / messageId.
  const msgId = resolveWaMsgSerializedId(msg);
  if (!msgId) return null;

  const chatId = String(
    msg?.id?.remote?._serialized ??
      msg?.from?._serialized ??
      msg?.chatId ??
      (typeof msg?.from === 'string' ? msg.from : '') ??
      '',
  );
  // senderId — for groups the participant JID; for 1:1 it equals chatId.
  const senderId = String(
    msg?.author?._serialized ??
      msg?.from?._serialized ??
      msg?.from?.id?._serialized ??
      (typeof msg?.from === 'string' ? msg.from : '') ??
      '',
  );
  const tSeconds = msg?.t ?? msg?.timestamp ?? 0;

  return {
    msgId,
    chatId,
    senderId,
    type: String(msg?.type ?? 'chat'),
    // For media messages `body` holds the thumbnail base64 (WAWeb quirk);
    // the human text lives in `caption`. Keep both so neither shadows the
    // other downstream.
    body: String(msg?.body ?? msg?.caption ?? ''),
    caption: String(msg?.caption ?? ''),
    // Trust the message TYPE, not just hasMedia/mediaKey: WA doesn't always have
    // those populated when we capture, which used to land image/video/doc rows
    // as hadMedia=false and silently exclude them from backup.
    hadMedia: Boolean(msg?.hasMedia || msg?.mediaKey || isBucketMediaType(msg?.type)),
    authorName: String(msg?.notifyName ?? ''),
    fromMe: Boolean(msg?.id?.fromMe ?? msg?.fromMe ?? false),
    whatsappTimestamp:
      typeof tSeconds === 'number' && tSeconds > 0 ? tSeconds * 1000 : Date.now(),
    // Omitted (not null) when absent, so a row for somebody else's message is
    // byte-identical to what this builder has always produced.
    ...(callerTokenOf(msg) ? { secret: callerTokenOf(msg) } : {}),
  };
};
